import { Box, Divider, Typography } from '@mui/material';
import { useMemo } from 'react';
import type { ViewsRestrictions } from '@/entities/session';
import { ViewAccessType, ViewType } from '@/entities/session';

const VIEW_GROUPS: Record<string, { name: string; value: ViewType }[]> = {
  Analytics: [
    {
      name: 'Cohort Marketing Performance',
      value: ViewType.cohortMarketingPerformance,
    },
    { name: 'Calendar Insights', value: ViewType.calendarInsights },
    { name: 'Cohort Chart', value: ViewType.cohortChart },
    { name: 'Calendar Chart', value: ViewType.calendarChart },
    { name: 'Cohort Scanner', value: ViewType.cohortScanner },
    { name: 'Cohort Retention', value: ViewType.cohortRetention },
    { name: 'ROI Breakdown', value: ViewType.roiBreakdown },
    { name: 'Overview', value: ViewType.overview },
  ],
  Administration: [
    { name: 'Integrations', value: ViewType.integrations },
    { name: 'Product Catalog', value: ViewType.productsCatalog },
  ],
};

const TOTAL_VIEWS = Object.values(VIEW_GROUPS).reduce(
  (sum, group) => sum + group.length,
  0,
);

interface Props {
  value: ViewsRestrictions;
}

export function ViewsRestrictionsPanelReadonly({ value }: Props) {
  const allowedGroups = useMemo(() => {
    return Object.entries(VIEW_GROUPS)
      .map(([groupName, options]) => ({
        groupName,
        items: options.filter(
          (opt) =>
            value[opt.value] == null ||
            value[opt.value] === ViewAccessType.ALLOWED,
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [value]);

  const allowedCount = useMemo(() => {
    return allowedGroups.reduce((sum, g) => sum + g.items.length, 0);
  }, [allowedGroups]);

  const allAllowed = useMemo(() => {
    return Object.values(value).every(
      (v) => v == null || v === ViewAccessType.ALLOWED,
    );
  }, [value]);

  const renderContent = () => {
    if (allAllowed) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="modalContent" sx={{ color: '#7E828F' }}>
            All views accessible
          </Typography>
        </Box>
      );
    }

    if (allowedGroups.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="modalContent" sx={{ color: '#7E828F' }}>
            No views accessible
          </Typography>
        </Box>
      );
    }

    return allowedGroups.map(({ groupName, items }) => (
      <Box key={groupName}>
        <Box
          sx={{
            px: 2,
            py: 1,
            fontSize: '0.875rem',
            lineHeight: '1rem',
            fontWeight: 500,
            color: '#343844',
            backgroundColor: '#F1F3F8',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          {groupName}
        </Box>
        {items.map((item) => (
          <Typography
            key={item.value}
            variant="modalContent"
            sx={{ px: 2, py: 1.25, display: 'block', color: '#343844' }}
          >
            {item.name}
          </Typography>
        ))}
      </Box>
    ));
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {allowedCount !== TOTAL_VIEWS && (
        <>
          <Divider />
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="modalContent" sx={{ color: '#7E828F' }}>
              Accessible:{' '}
              <Box
                component="span"
                sx={{ color: (theme) => theme.palette.primary.main }}
              >
                {allowedCount}
              </Box>{' '}
              of {TOTAL_VIEWS}
            </Typography>
          </Box>
        </>
      )}
      <Divider />
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>{renderContent()}</Box>
    </Box>
  );
}
